import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Heading, PlusIcon } from '@quickmd-llc/design-system'
import { LICENSE_ADD_ROUTE, LICENSE_EDIT_ROUTE } from '@/utils/constants'
import type { DoctorLicenseAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import type { License } from '../license-form/transformLicense'
import { LicenseCard } from '../license-card/LicenseCard'
import { transformApiLicense } from '../license-form/transformLicense'
import { getStateName } from '../getStateName'
import {
  ProductivitySection,
  type ProductivityVisitType,
  type SaveProductivityArgs,
} from '../productivity-section/ProductivitySection'

interface LicensesTabProps {
  licenses: DoctorLicenseAdminApiOutContract[]
  serviceTypes: { VisitTypeId: string; Name: string }[]
  userId: string
  onDeleteLicense: (license: License) => void
  onVisibilityChange?: (license: License, visibility: string) => void
  productivityVisitTypes: ProductivityVisitType[]
  onSaveProductivity: (args: SaveProductivityArgs) => Promise<boolean>
  onResetProductivity: (visitTypeId: string) => Promise<boolean>
  isProductivitySaving?: boolean
}

export function LicensesTab({
  licenses,
  serviceTypes,
  userId,
  onDeleteLicense,
  onVisibilityChange,
  productivityVisitTypes,
  onSaveProductivity,
  onResetProductivity,
  isProductivitySaving,
}: LicensesTabProps) {
  const navigate = useNavigate()

  const transformedLicenses = useMemo(
    () =>
      licenses
        .map((l) => {
          const license = transformApiLicense(l)
          return { license, stateName: getStateName(license.StateCode) }
        })
        .sort((a, b) => a.stateName.localeCompare(b.stateName))
        .map(({ license }) => license),
    [licenses],
  )

  function handleAddLicense() {
    navigate(`${LICENSE_ADD_ROUTE}?id=${userId}`)
  }

  function handleEditLicense(license: License) {
    navigate(`${LICENSE_EDIT_ROUTE}?id=${userId}&licenseId=${license.LicenseId}`)
  }

  return (
    <div className='flex w-full flex-1 flex-col gap-sp-3'>
      {/* Licenses section */}
      <div className='flex flex-1 flex-col gap-sp-2'>
        <div className='flex items-center gap-sp-2'>
          <Heading level='h3' className='flex-1'>
            Licenses
          </Heading>
          <Button onClick={handleAddLicense}>
            <PlusIcon iconSize='s' />
            Add license
          </Button>
        </div>

        {transformedLicenses.length > 0 ?
          <div className='grid grid-cols-2 gap-sp-2'>
            {transformedLicenses.map((license) => (
              // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered
              <LicenseCard
                key={license.LicenseId}
                license={license}
                serviceTypes={serviceTypes}
                onEdit={handleEditLicense}
                onDelete={onDeleteLicense}
                onVisibilityChange={onVisibilityChange}
              />
            ))}
          </div>
        : <div className='flex flex-1 flex-col items-center justify-center rounded-lg border border-shade-10 bg-white p-sp-2'>
            <div className='flex items-center justify-center rounded-full bg-[#e3e5e6] p-[6px]'>
              <svg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <rect x='4' y='10' width='40' height='28' rx='3' stroke='#253845' strokeWidth='2.5' />
                <circle cx='17' cy='22' r='4' stroke='#253845' strokeWidth='2' />
                <path d='M11 34c0-3.3 2.7-6 6-6s6 2.7 6 6' stroke='#253845' strokeWidth='2' strokeLinecap='round' />
                <line x1='28' y1='18' x2='38' y2='18' stroke='#253845' strokeWidth='2' strokeLinecap='round' />
                <line x1='28' y1='24' x2='38' y2='24' stroke='#253845' strokeWidth='2' strokeLinecap='round' />
                <line x1='28' y1='30' x2='34' y2='30' stroke='#253845' strokeWidth='2' strokeLinecap='round' />
              </svg>
            </div>
            <p className='mt-sp-2 text-base tracking-[0.12px] text-text-light'>Begin adding Licenses</p>
          </div>
        }
      </div>

      {/* Productivity section — only shown when licenses exist */}
      {transformedLicenses.length > 0 && (
        <div className='flex flex-col gap-sp-2'>
          <Heading level='h3'>Productivity</Heading>
          {productivityVisitTypes.length > 0 ?
            <ProductivitySection
              visitTypes={productivityVisitTypes}
              onSave={onSaveProductivity}
              onReset={onResetProductivity}
              isSaving={isProductivitySaving ?? false}
            />
          : <p className='text-sm tracking-[0.12px] text-text-medium'>No productivity data.</p>}
        </div>
      )}
    </div>
  )
}
